import { createFileRoute } from "@tanstack/react-router";
import heroBg from "@/assets/heroes/colorado-local-seo-hero.jpg";
import { Search } from "lucide-react";
import { StatewideServiceHub } from "@/components/StatewideServiceHub";
import { buildCityCards } from "@/lib/statewideServiceCities";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { buildServiceHubScripts } from "@/lib/serviceHubSchema";

const CANONICAL = "https://coloradowebimpressions.com/local-seo";
const OG_IMAGE = `https://coloradowebimpressions.com${ogImageFor({ service: "seo" })}`;

export const Route = createFileRoute("/local-seo")({
  head: () => ({
    meta: [
      { title: "Colorado Local SEO That Wins the Map Pack — Since 2006" },
      {
        name: "description",
        content:
          "Local SEO for every major Colorado city — Denver, Springs, Boulder, Fort Collins, Pueblo & more. Map Pack rankings in 60–120 days. Owner-led since 2006.",
      },
      { property: "og:title", content: "Colorado Local SEO That Wins the Map Pack — Since 2006" },
      { property: "og:description", content: "Local SEO for every major Colorado city. Map Pack rankings in 60–120 days, owner-led since 2006." },
      { property: "og:url", content: CANONICAL },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "Colorado Local SEO — Wins the Map Pack",
        description: "Local SEO programs for every major Colorado city. Map Pack in 60–120 days.",
      }),
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: buildServiceHubScripts({
      path: "/local-seo",
      name: "Local SEO",
      description: "City-tuned local SEO programs for Colorado businesses across Denver, Colorado Springs, Boulder, Fort Collins, Pueblo, Aurora and the Front Range.",
      serviceType: "Search Engine Optimization",
      priceRange: "$$",
      faqs: [
        { q: "How long does local SEO take to work in Colorado?", a: "Most clients see Map Pack movement in 60–120 days and meaningful lead growth between months 4 and 9. Competitive cities like Denver and Boulder lean toward the longer end." },
        { q: "How much does local SEO cost?", a: "Standalone local SEO runs $800–$1,800/month depending on city competitiveness and how many service pages you need. Our full bundle is $2,300/month." },
        { q: "Will you optimize my Google Business Profile?", a: "Yes — GBP optimization is the highest-leverage part of local SEO. Weekly posts, geo-tagged photos, review velocity, Q&A management, and category tuning are all included." },
        { q: "Do you build local citations and directory listings?", a: "Yes. We submit and verify your NAP (name, address, phone) across the top 40+ directories Google trusts for Colorado businesses, plus industry-specific listings." },
      ],
    }),
  }),
  component: () => (
    <StatewideServiceHub
      heroImage={{ src: heroBg, alt: "Colorado local SEO and Google Maps search on a phone downtown — Colorado Web Impressions" }}
      service="SEO"
      serviceLower="local SEO"
      eyebrow="Colorado Local SEO"
      headline="Local SEO that ranks in your Colorado city — not someone else's"
      goldWord="ranks"
      subheadline="We build SEO programs for one Colorado city at a time. Same playbook, different on-page strategy for every market we serve."
      benefits={[
        "Built for the way your city actually searches",
        "On-page SEO + Map Pack + AI search in one program",
        "Owner-led — direct access, no junior account managers",
        "No long contracts, no setup games, transparent pricing",
        "100+ Colorado businesses ranked across the Front Range",
      ]}
      benefitIcon={Search}
      servicesGrid
      industriesGrid
      cities={buildCityCards("seo", "/colorado-springs-seo")}
      pickerHeading="Pick your Colorado city for SEO"
      closing="Don't see your city? We still serve you. Use the contact button below and we'll build a custom SEO plan for your specific Colorado market."
    />
  ),
});
